// frontend/src/lib/api.ts
// Direct REST API client that bypasses Supabase JS client
// This ensures consistent fast performance on both desktop and mobile

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

interface ApiOptions {
  token?: string;
  headers?: Record<string, string>;
}

interface QueryOptions extends ApiOptions {
  select?: string;
  filters?: Record<string, string>;
  order?: string;
  limit?: number;
  single?: boolean;
  maybeSingle?: boolean; // Like single, but returns null instead of error when no rows found
  count?: 'exact' | 'planned' | 'estimated';
  head?: boolean;
}

interface MutationOptions extends ApiOptions {
  returning?: boolean;
}

function buildHeaders(token?: string, extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': supabaseAnonKey,
    'Authorization': `Bearer ${token || supabaseAnonKey}`,
    ...extra,
  };
  return headers;
}

function buildQueryString(options: QueryOptions): string {
  const params: string[] = [];
  
  if (options.select) {
    params.push(`select=${encodeURIComponent(options.select)}`);
  }
  
  if (options.filters) {
    Object.entries(options.filters).forEach(([key, value]) => {
      params.push(`${key}=${encodeURIComponent(value)}`);
    });
  }
  
  if (options.order) {
    params.push(`order=${encodeURIComponent(options.order)}`);
  }
  
  if (options.limit) {
    params.push(`limit=${options.limit}`);
  }
  
  return params.length > 0 ? `?${params.join('&')}` : '';
}

// GET - Select data
export async function dbSelect<T>(
  table: string,
  options: QueryOptions = {}
): Promise<{ data: T | null; error: Error | null; count?: number }> {
  try {
    // For maybeSingle, add limit=1 if not already set
    const queryOptions = { ...options };
    if (options.maybeSingle && !options.limit) {
      queryOptions.limit = 1;
    }
    
    const queryString = buildQueryString({ select: '*', ...queryOptions });
    const headers = buildHeaders(options.token);
    
    if (options.single) {
      headers['Accept'] = 'application/vnd.pgrst.object+json';
    }
    
    if (options.count) {
      headers['Prefer'] = `count=${options.count}`;
    }
    
    if (options.head) {
      headers['Prefer'] = headers['Prefer'] ? `${headers['Prefer']}, head=true` : 'head=true';
    }
    
    const response = await fetch(`${supabaseUrl}/rest/v1/${table}${queryString}`, {
      method: options.head ? 'HEAD' : 'GET',
      headers,
    });
    
    if (!response.ok) {
      // For maybeSingle, treat 406 (Not Acceptable) as "no data found"
      if (options.maybeSingle && response.status === 406) {
        return { data: null, error: null };
      }
      const errorText = await response.text();
      return { data: null, error: new Error(errorText || `HTTP ${response.status}`) };
    }
    
    // Extract count from header if requested
    let count: number | undefined;
    if (options.count) {
      const contentRange = response.headers.get('content-range');
      if (contentRange) {
        const match = contentRange.match(/\/(\d+)/);
        if (match) {
          count = parseInt(match[1], 10);
        }
      }
    }
    
    if (options.head) {
      return { data: null, error: null, count };
    }
    
    const data = await response.json();
    
    // For maybeSingle, extract first item from array or return null
    if (options.maybeSingle) {
      const firstItem = Array.isArray(data) ? data[0] || null : data;
      return { data: firstItem, error: null, count };
    }
    
    return { data, error: null, count };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error('Unknown error') };
  }
}

// POST - Insert data
export async function dbInsert<T>(
  table: string,
  data: Record<string, unknown> | Record<string, unknown>[],
  options: MutationOptions = {}
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const headers = buildHeaders(options.token);
    
    if (options.returning !== false) {
      headers['Prefer'] = 'return=representation';
    }
    
    const response = await fetch(`${supabaseUrl}/rest/v1/${table}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      return { data: null, error: new Error(errorText || `HTTP ${response.status}`) };
    }
    
    if (options.returning === false) {
      return { data: null, error: null };
    }
    
    const result = await response.json();
    // If single item inserted, return first element
    return { data: Array.isArray(result) ? result[0] : result, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error('Unknown error') };
  }
}

// PATCH - Update data
export async function dbUpdate<T>(
  table: string,
  data: Record<string, unknown>,
  filters: Record<string, string>,
  options: MutationOptions = {}
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const queryString = buildQueryString({ filters });
    const headers = buildHeaders(options.token);
    
    if (options.returning !== false) {
      headers['Prefer'] = 'return=representation';
    }
    
    const response = await fetch(`${supabaseUrl}/rest/v1/${table}${queryString}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      return { data: null, error: new Error(errorText || `HTTP ${response.status}`) };
    }
    
    if (options.returning === false) {
      return { data: null, error: null };
    }
    
    const result = await response.json();
    return { data: Array.isArray(result) ? result[0] : result, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error('Unknown error') };
  }
}

// DELETE - Delete data
export async function dbDelete(
  table: string,
  filters: Record<string, string>,
  options: ApiOptions = {}
): Promise<{ error: Error | null }> {
  try {
    const queryString = buildQueryString({ filters });
    const headers = buildHeaders(options.token);
    
    const response = await fetch(`${supabaseUrl}/rest/v1/${table}${queryString}`, {
      method: 'DELETE',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      return { error: new Error(errorText || `HTTP ${response.status}`) };
    }
    
    return { error: null };
  } catch (err) {
    return { error: err instanceof Error ? err : new Error('Unknown error') };
  }
}

// RPC - Call database function
export async function dbRpc<T>(
  functionName: string,
  params: Record<string, unknown> = {},
  options: ApiOptions = {}
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const headers = buildHeaders(options.token);
    
    const response = await fetch(`${supabaseUrl}/rest/v1/rpc/${functionName}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      return { data: null, error: new Error(errorText || `HTTP ${response.status}`) };
    }
    
    const data = await response.json();
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error('Unknown error') };
  }
}

// Invoke Edge Function
export async function invokeFunction<T>(
  functionName: string,
  body: Record<string, unknown> = {},
  options: ApiOptions = {}
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${options.token || supabaseAnonKey}`,
    };
    
    const response = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      return { data: null, error: new Error(errorText || `HTTP ${response.status}`) };
    }
    
    const data = await response.json();
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error('Unknown error') };
  }
}

// Storage operations
export async function uploadFile(
  bucket: string,
  path: string,
  file: File | Blob,
  options: ApiOptions = {}
): Promise<{ error: Error | null }> {
  try {
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${options.token || supabaseAnonKey}`,
    };
    
    const response = await fetch(`${supabaseUrl}/storage/v1/object/${bucket}/${path}`, {
      method: 'POST',
      headers,
      body: file,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      return { error: new Error(errorText || `HTTP ${response.status}`) };
    }
    
    return { error: null };
  } catch (err) {
    return { error: err instanceof Error ? err : new Error('Unknown error') };
  }
}

// Get session from localStorage synchronously
export function getStoredSession(): { accessToken: string; userId: string } | null {
  try {
    const projectRef = supabaseUrl?.split('//')[1]?.split('.')[0];
    const storageKey = `sb-${projectRef}-auth-token`;
    const stored = localStorage.getItem(storageKey);
    
    if (stored) {
      const parsed = JSON.parse(stored);
      const sessionData = parsed.currentSession || parsed;
      const accessToken = sessionData?.access_token;
      const userId = sessionData?.user?.id;
      
      if (accessToken && userId) {
        return { accessToken, userId };
      }
    }
  } catch {
    // Ignore errors
  }
  return null;
}